import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined';
import AddBusinessOutlinedIcon from '@mui/icons-material/AddBusinessOutlined';
import { showSoftSuccess } from '../utils/softSuccess';
import EngineeringOutlinedIcon from '@mui/icons-material/EngineeringOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LockResetOutlinedIcon from '@mui/icons-material/LockResetOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';

import { fieldStaffApi } from '../api/fieldStaff';

const roleLabels = {
  installer: 'Монтажник',
  pnr_engineer: 'Специалист ПНР',
  field_engineer: 'Инженер',
};

const statusLabels = {
  draft: 'Подготовка',
  installation: 'Монтаж',
  commissioning: 'ПНР',
  rework: 'Доработка',
  ready_for_handover: 'Готов к передаче',
  active: 'Активен',
  archived: 'Архив',
};

const errorText = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => item?.msg || item?.message)
      .filter(Boolean)
      .join('. ') || fallback;
  }
  return error?.message || fallback;
};

const cardSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 3,
  boxShadow: 'none',
};

const FieldStaffPage = () => {
  const [staff, setStaff] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [accesses, setAccesses] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [accessLoading, setAccessLoading] = useState(false);
  const [buildingsLoading, setBuildingsLoading] = useState(false);
  const [progressLoading, setProgressLoading] = useState(false);
  const [progressTenantId, setProgressTenantId] = useState('');
  const [structureProgress, setStructureProgress] = useState([]);
  const [saving, setSaving] = useState(false);
  const [workspaceDialogOpen, setWorkspaceDialogOpen] = useState(false);
  const [staffDialogOpen, setStaffDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [createForm, setCreateForm] = useState({
    email: '',
    full_name: '',
    password: '',
    role: 'pnr_engineer',
  });
  const [accessForm, setAccessForm] = useState({
    tenant_id: '',
    access_level: 'write',
    building_ids: [],
    all_buildings: true,
  });
  const [workspaceForm, setWorkspaceForm] = useState({
    name: '',
  });
  const [staffForm, setStaffForm] = useState({
    full_name: '',
    role: 'pnr_engineer',
    is_active: true,
  });
  const [passwordForm, setPasswordForm] = useState({ password: '', confirmation: '' });

  const loadInitial = async () => {
    setLoading(true);
    try {
      const [staffItems, workspaceItems] = await Promise.all([
        fieldStaffApi.listStaff(),
        fieldStaffApi.listWorkspaces(),
      ]);
      setStaff(staffItems);
      setWorkspaces(workspaceItems);
      if (!progressTenantId && workspaceItems.length) {
        setProgressTenantId(workspaceItems[0].id);
      }
      if (!selectedId && staffItems.length) setSelectedId(staffItems[0].id);
    } catch (error) {
      setMessage({ type: 'error', text: errorText(error, 'Не удалось загрузить данные') });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInitial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setAccesses([]);
      return;
    }
    setAccessLoading(true);
    fieldStaffApi
      .listAccess(selectedId)
      .then(setAccesses)
      .catch((error) =>
        setMessage({ type: 'error', text: errorText(error, 'Не удалось загрузить доступы') }),
      )
      .finally(() => setAccessLoading(false));
  }, [selectedId]);

  useEffect(() => {
    const tenantId = accessForm.tenant_id;
    setBuildings([]);
    if (!tenantId || accessForm.all_buildings) return;
    setBuildingsLoading(true);
    fieldStaffApi
      .listBuildings(tenantId)
      .then(setBuildings)
      .catch((error) =>
        setMessage({ type: 'error', text: errorText(error, 'Не удалось загрузить дома') }),
      )
      .finally(() => setBuildingsLoading(false));
  }, [accessForm.all_buildings, accessForm.tenant_id]);

  const loadStructureProgress = async (tenantId = progressTenantId) => {
    if (!tenantId) {
      setStructureProgress([]);
      return;
    }
    setProgressLoading(true);
    try {
      setStructureProgress(await fieldStaffApi.listStructureProgress(tenantId));
    } catch (error) {
      setMessage({ type: 'error', text: errorText(error, 'Не удалось загрузить ход работ') });
    } finally {
      setProgressLoading(false);
    }
  };

  useEffect(() => {
    loadStructureProgress(progressTenantId);
    if (!progressTenantId) return undefined;
    const timer = window.setInterval(() => {
      fieldStaffApi
        .listStructureProgress(progressTenantId)
        .then(setStructureProgress)
        .catch(() => undefined);
    }, 30000);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progressTenantId]);

  const filteredStaff = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return staff;
    return staff.filter((item) =>
      `${item.full_name || ''} ${item.email}`.toLowerCase().includes(query),
    );
  }, [search, staff]);

  const selectedStaff = staff.find((item) => item.id === selectedId);

  const openStaffDialog = () => {
    if (!selectedStaff) return;
    setStaffForm({
      full_name: selectedStaff.full_name || '',
      role: selectedStaff.role,
      is_active: selectedStaff.is_active,
    });
    setStaffDialogOpen(true);
  };

  const updateSelectedStaff = async (event) => {
    event.preventDefault();
    if (!selectedStaff) return;
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const updated = await fieldStaffApi.updateStaff(selectedStaff.id, {
        full_name: staffForm.full_name.trim(),
        role: staffForm.role,
        is_active: staffForm.is_active,
      });
      setStaff((items) => items.map((item) => (item.id === updated.id ? updated : item)));
      setAccesses(await fieldStaffApi.listAccess(updated.id));
      setStaffDialogOpen(false);
      setMessage({ type: 'success', text: 'Данные сотрудника сохранены' });
      showSoftSuccess('Данные сотрудника сохранены');
    } catch (error) {
      setMessage({ type: 'error', text: errorText(error, 'Не удалось сохранить сотрудника') });
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async (event) => {
    event.preventDefault();
    if (!selectedStaff) return;
    if (passwordForm.password.length < 8) {
      setMessage({ type: 'error', text: 'Пароль должен содержать не менее 8 символов' });
      return;
    }
    if (passwordForm.password !== passwordForm.confirmation) {
      setMessage({ type: 'error', text: 'Пароли не совпадают' });
      return;
    }
    setSaving(true);
    try {
      await fieldStaffApi.updateStaff(selectedStaff.id, { password: passwordForm.password });
      setPasswordForm({ password: '', confirmation: '' });
      setPasswordDialogOpen(false);
      setMessage({ type: 'success', text: 'Пароль изменён. Активные сессии завершены' });
      showSoftSuccess('Пароль изменён');
    } catch (error) {
      setMessage({ type: 'error', text: errorText(error, 'Не удалось изменить пароль') });
    } finally {
      setSaving(false);
    }
  };

  const deleteSelectedStaff = async () => {
    if (!selectedStaff) return;
    setSaving(true);
    try {
      await fieldStaffApi.deleteStaff(selectedStaff.id);
      const remaining = staff.filter((item) => item.id !== selectedStaff.id);
      setStaff(remaining);
      setSelectedId(remaining[0]?.id || '');
      setAccesses([]);
      setDeleteDialogOpen(false);
      setMessage({ type: 'success', text: 'Сотрудник удалён' });
      showSoftSuccess('Сотрудник удалён');
    } catch (error) {
      setMessage({ type: 'error', text: errorText(error, 'Не удалось удалить сотрудника') });
    } finally {
      setSaving(false);
    }
  };

  const editAccess = (access) => {
    setAccessForm({
      tenant_id: access.tenant_id,
      access_level: access.access_level,
      building_ids: access.building_ids,
      all_buildings: access.building_ids.length === 0,
    });
  };

  const createStaff = async (event) => {
    event.preventDefault();
    if (!createForm.email.trim() || createForm.password.length < 8) {
      setMessage({ type: 'error', text: 'Укажите email и пароль не короче 8 символов' });
      return;
    }
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const created = await fieldStaffApi.createStaff({
        ...createForm,
        email: createForm.email.trim(),
        full_name: createForm.full_name.trim(),
      });
      setStaff((items) => [created, ...items]);
      setSelectedId(created.id);
      setCreateForm({ email: '', full_name: '', password: '', role: 'pnr_engineer' });
      setMessage({ type: 'success', text: `${roleLabels[created.role]} добавлен` });
      showSoftSuccess(`${roleLabels[created.role]} добавлен`);
    } catch (error) {
      setMessage({ type: 'error', text: errorText(error, 'Не удалось создать сотрудника') });
    } finally {
      setSaving(false);
    }
  };

  const grantAccess = async () => {
    if (!selectedId || !accessForm.tenant_id) {
      setMessage({ type: 'error', text: 'Выберите сотрудника и объект' });
      return;
    }
    if (!accessForm.all_buildings && accessForm.building_ids.length === 0) {
      setMessage({ type: 'error', text: 'Выберите хотя бы один дом' });
      return;
    }
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      await fieldStaffApi.grantAccess({
        user_id: selectedId,
        tenant_id: accessForm.tenant_id,
        access_level: accessForm.access_level,
        building_ids: accessForm.all_buildings ? [] : accessForm.building_ids,
      });
      setAccesses(await fieldStaffApi.listAccess(selectedId));
      setMessage({ type: 'success', text: 'Доступ сохранён' });
      showSoftSuccess('Доступ сохранён');
    } catch (error) {
      setMessage({ type: 'error', text: errorText(error, 'Не удалось сохранить доступ') });
    } finally {
      setSaving(false);
    }
  };

  const revokeAccess = async (accessId) => {
    setSaving(true);
    try {
      await fieldStaffApi.revokeAccess(accessId);
      setAccesses((items) =>
        items.map((item) =>
          item.id === accessId ? { ...item, revoked_at: new Date().toISOString() } : item,
        ),
      );
      setMessage({ type: 'success', text: 'Доступ отозван' });
    } catch (error) {
      setMessage({ type: 'error', text: errorText(error, 'Не удалось отозвать доступ') });
    } finally {
      setSaving(false);
    }
  };

  const createWorkspace = async (event) => {
    event.preventDefault();
    const name = workspaceForm.name.trim();
    if (!name) {
      setMessage({
        type: 'error',
        text: 'Укажите название объекта',
      });
      return;
    }
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const created = await fieldStaffApi.createWorkspace({
        name,
      });
      setWorkspaces((items) => [created, ...items]);
      setAccessForm((form) => ({
        ...form,
        tenant_id: created.id,
        all_buildings: true,
        building_ids: [],
      }));
      setWorkspaceForm({ name: '' });
      setWorkspaceDialogOpen(false);
      setMessage({ type: 'success', text: 'Объект добавлен' });
      showSoftSuccess('Объект добавлен');
    } catch (error) {
      setMessage({ type: 'error', text: errorText(error, 'Не удалось создать объект') });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, width: '100%', minWidth: 0 }}>
      {message.text && (
        <Alert severity={message.type || 'info'} onClose={() => setMessage({ type: '', text: '' })} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      )}

      <Paper component="form" onSubmit={createStaff} sx={{ ...cardSx, p: { xs: 2, md: 2.25 }, mb: 2 }}>
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 38, height: 38 }}>
            <PersonAddAltOutlinedIcon fontSize="small" />
          </Avatar>
          <Typography variant="h6" fontWeight={750}>Новый сотрудник</Typography>
        </Stack>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} sm={6} xl={3}>
            <TextField fullWidth size="small" label="ФИО" value={createForm.full_name} onChange={(event) => setCreateForm((form) => ({ ...form, full_name: event.target.value }))} />
          </Grid>
          <Grid item xs={12} sm={6} xl={3}>
            <TextField fullWidth size="small" label="Email" type="email" value={createForm.email} onChange={(event) => setCreateForm((form) => ({ ...form, email: event.target.value }))} />
          </Grid>
          <Grid item xs={12} sm={6} xl={2}>
            <TextField fullWidth size="small" label="Пароль" type="password" value={createForm.password} onChange={(event) => setCreateForm((form) => ({ ...form, password: event.target.value }))} />
          </Grid>
          <Grid item xs={12} sm={6} xl={2}>
            <FormControl size="small" fullWidth>
              <InputLabel>Роль</InputLabel>
              <Select value={createForm.role} label="Роль" onChange={(event) => setCreateForm((form) => ({ ...form, role: event.target.value }))}>
                <MenuItem value="pnr_engineer">Специалист ПНР</MenuItem>
                <MenuItem value="installer">Монтажник</MenuItem>
                <MenuItem value="field_engineer">Инженер</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} xl={2}>
            <Button fullWidth type="submit" variant="contained" disabled={saving} sx={{ minHeight: 40 }}>
              Создать сотрудника
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ ...cardSx, p: { xs: 2, md: 2.25 }, mb: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', md: 'center' }}
          sx={{ mb: structureProgress.length || progressLoading ? 1.5 : 0 }}
        >
          <ApartmentOutlinedIcon color="primary" />
          <Typography variant="h6" fontWeight={750} sx={{ flex: 1 }}>
            Ход работ
          </Typography>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 300 } }}>
            <InputLabel>Объект</InputLabel>
            <Select
              value={progressTenantId}
              label="Объект"
              onChange={(event) => setProgressTenantId(event.target.value)}
            >
              {workspaces.map((item) => (
                <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Обновить">
            <span>
              <IconButton
                onClick={() => loadStructureProgress()}
                disabled={!progressTenantId || progressLoading}
              >
                <RefreshRoundedIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
        {progressLoading ? (
          <Stack alignItems="center" sx={{ py: 2 }}><CircularProgress size={26} /></Stack>
        ) : structureProgress.length ? (
          <Stack spacing={0.75}>
            {structureProgress.map((item) => (
              <Stack
                key={item.building_id}
                direction={{ xs: 'column', md: 'row' }}
                alignItems={{ xs: 'flex-start', md: 'center' }}
                spacing={{ xs: 0.5, md: 1.5 }}
                sx={{ px: 1.5, py: 1.1, borderRadius: 2, bgcolor: 'action.hover' }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography fontWeight={700} noWrap>{item.building_name}</Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {item.updated_at
                      ? `${item.updated_by_name || 'Сотрудник не указан'} · ${new Date(item.updated_at).toLocaleString('ru-RU')}`
                      : item.building_address || 'Работы не начаты'}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  <Chip size="small" variant="outlined" label={`Корпусов ${item.corps}`} />
                  <Chip size="small" variant="outlined" label={`Подъездов ${item.entrances}`} />
                  <Chip size="small" variant="outlined" label={`Этажей ${item.floors}`} />
                  <Chip size="small" color="primary" variant="outlined" label={`Помещений ${item.apartments}`} />
                </Stack>
                <Chip
                  size="small"
                  color={item.status === 'applied' ? 'success' : item.status === 'draft' ? 'warning' : 'default'}
                  label={item.status === 'applied' ? 'Сохранено' : item.status === 'draft' ? 'Черновик' : 'Не начато'}
                />
              </Stack>
            ))}
          </Stack>
        ) : progressTenantId ? (
          <Typography variant="body2" color="text.secondary">
            Черновиков пока нет
          </Typography>
        ) : null}
      </Paper>

      <Grid container spacing={2} alignItems="stretch">
        <Grid item xs={12} lg={4} sx={{ display: 'flex' }}>
            <Paper sx={{ ...cardSx, p: 1.5, minHeight: 560, width: '100%' }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Поиск сотрудника"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                InputProps={{ startAdornment: <SearchRoundedIcon color="action" sx={{ mr: 1 }} /> }}
              />
              {loading ? (
                <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress size={28} /></Stack>
              ) : (
                <List dense sx={{ mt: 1, maxHeight: 390, overflowY: 'auto' }}>
                  {filteredStaff.map((item) => (
                    <ListItemButton key={item.id} selected={item.id === selectedId} onClick={() => setSelectedId(item.id)} sx={{ borderRadius: 2, mb: 0.5, opacity: item.is_active ? 1 : 0.6 }}>
                      <Avatar sx={{ width: 34, height: 34, mr: 1.25, bgcolor: item.role === 'pnr_engineer' ? 'success.light' : 'primary.light' }}>
                        {item.role === 'pnr_engineer' ? <FactCheckOutlinedIcon fontSize="small" /> : <EngineeringOutlinedIcon fontSize="small" />}
                      </Avatar>
                      <ListItemText
                        primary={item.full_name || item.email}
                        secondary={`${roleLabels[item.role]} · ${item.email}${item.is_active ? '' : ' · Отключён'}`}
                      />
                    </ListItemButton>
                  ))}
                  {!filteredStaff.length && <Typography color="text.secondary" sx={{ p: 2 }}>Сотрудники не найдены</Typography>}
                </List>
              )}
            </Paper>
        </Grid>

        <Grid item xs={12} lg={8} sx={{ display: 'flex' }}>
          <Paper sx={{ ...cardSx, p: { xs: 2, md: 2.5 }, minHeight: 560, width: '100%' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1.25} sx={{ mb: 2 }}>
              <AssignmentIndOutlinedIcon color="primary" />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" fontWeight={750}>Назначения</Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedStaff ? `${selectedStaff.full_name || selectedStaff.email} · ${roleLabels[selectedStaff.role]}` : 'Выберите сотрудника'}
                </Typography>
              </Box>
              {selectedStaff && (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Tooltip title="Редактировать сотрудника">
                    <IconButton size="small" onClick={openStaffDialog}>
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Сменить пароль">
                    <IconButton size="small" onClick={() => setPasswordDialogOpen(true)}>
                      <LockResetOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Удалить сотрудника">
                    <IconButton size="small" color="error" onClick={() => setDeleteDialogOpen(true)}>
                      <DeleteOutlineRoundedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              )}
              <Button size="small" variant="outlined" startIcon={<AddBusinessOutlinedIcon />} onClick={() => setWorkspaceDialogOpen(true)}>
                Новый объект
              </Button>
            </Stack>
            <Grid container spacing={1.5}>
              <Grid item xs={12} md={6}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Объект</InputLabel>
                  <Select value={accessForm.tenant_id} label="Объект" onChange={(event) => setAccessForm((form) => ({ ...form, tenant_id: event.target.value, building_ids: [] }))}>
                    {!workspaces.length && (
                      <MenuItem disabled value="">Нет доступных объектов</MenuItem>
                    )}
                    {workspaces.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%" gap={2}>
                          <span>{item.name}</span>
                          <Chip size="small" variant="outlined" label={statusLabels[item.lifecycle_status] || item.lifecycle_status} />
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Уровень доступа</InputLabel>
                  <Select value={accessForm.access_level} label="Уровень доступа" onChange={(event) => setAccessForm((form) => ({ ...form, access_level: event.target.value }))}>
                    <MenuItem value="write">Работа с объектом</MenuItem>
                    <MenuItem value="read">Только просмотр</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Checkbox checked={accessForm.all_buildings} onChange={(event) => setAccessForm((form) => ({ ...form, all_buildings: event.target.checked, building_ids: [] }))} />
                <Typography component="span" variant="body2">Все дома объекта</Typography>
              </Grid>
              {!accessForm.all_buildings && (
                <Grid item xs={12}>
                  <FormControl size="small" fullWidth disabled={!accessForm.tenant_id || buildingsLoading}>
                    <InputLabel>Дома</InputLabel>
                    <Select
                      multiple
                      value={accessForm.building_ids}
                      onChange={(event) => setAccessForm((form) => ({ ...form, building_ids: event.target.value }))}
                      input={<OutlinedInput label="Дома" />}
                      renderValue={(selected) => `Выбрано: ${selected.length}`}
                    >
                      {!buildingsLoading && !buildings.length && (
                        <MenuItem disabled value="">В объекте пока нет домов</MenuItem>
                      )}
                      {buildings.map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          <Checkbox checked={accessForm.building_ids.includes(item.id)} />
                          <ListItemText primary={item.name} secondary={item.address} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12}>
                <Button variant="contained" onClick={grantAccess} disabled={saving || !selectedId || !accessForm.tenant_id}>
                  Сохранить назначение
                </Button>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2.5 }} />
            <Typography variant="subtitle1" fontWeight={750} sx={{ mb: 1 }}>Текущие доступы</Typography>
            {accessLoading ? (
              <CircularProgress size={26} />
            ) : accesses.length ? (
              <Stack spacing={1}>
                {accesses.map((item) => (
                  <Box key={item.id} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, opacity: item.revoked_at ? 0.55 : 1 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1}>
                      <Box sx={{ flex: 1 }}>
                        <Typography fontWeight={700}>{item.tenant_name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {item.building_ids.length ? `Домов: ${item.building_ids.length}` : 'Все дома'} · {item.access_level === 'write' ? 'Работа с объектом' : 'Просмотр'}
                        </Typography>
                      </Box>
                      <Chip size="small" color={item.revoked_at ? 'default' : 'success'} variant="outlined" label={item.revoked_at ? 'Отозван' : 'Активен'} />
                      {!item.revoked_at && <Button size="small" onClick={() => editAccess(item)}>Настроить</Button>}
                      {!item.revoked_at && <Button size="small" color="error" onClick={() => revokeAccess(item.id)}>Отозвать</Button>}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            ) : (
              <Typography color="text.secondary">Назначений пока нет</Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Dialog
        open={staffDialogOpen}
        onClose={() => !saving && setStaffDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ component: 'form', onSubmit: updateSelectedStaff }}
      >
        <DialogTitle>Настройка сотрудника</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              autoFocus
              fullWidth
              label="ФИО"
              value={staffForm.full_name}
              onChange={(event) => setStaffForm((form) => ({ ...form, full_name: event.target.value }))}
            />
            <FormControl fullWidth>
              <InputLabel>Роль</InputLabel>
              <Select
                value={staffForm.role}
                label="Роль"
                onChange={(event) => setStaffForm((form) => ({ ...form, role: event.target.value }))}
              >
                <MenuItem value="installer">Монтажник</MenuItem>
                <MenuItem value="pnr_engineer">Специалист ПНР</MenuItem>
                <MenuItem value="field_engineer">Инженер</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={staffForm.is_active}
                  onChange={(event) => setStaffForm((form) => ({ ...form, is_active: event.target.checked }))}
                />
              )}
              label="Сотрудник может входить в приложение"
            />
            {!staffForm.is_active && (
              <Alert severity="warning">Активные сессии и назначения сотрудника будут отозваны.</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setStaffDialogOpen(false)} disabled={saving}>Отмена</Button>
          <Button type="submit" variant="contained" disabled={saving}>Сохранить</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={passwordDialogOpen}
        onClose={() => !saving && setPasswordDialogOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ component: 'form', onSubmit: changePassword }}
      >
        <DialogTitle>Смена пароля</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              autoFocus
              fullWidth
              type="password"
              label="Новый пароль"
              value={passwordForm.password}
              onChange={(event) => setPasswordForm((form) => ({ ...form, password: event.target.value }))}
              helperText="Не менее 8 символов"
            />
            <TextField
              fullWidth
              type="password"
              label="Повторите пароль"
              value={passwordForm.confirmation}
              onChange={(event) => setPasswordForm((form) => ({ ...form, confirmation: event.target.value }))}
            />
            <Alert severity="info">После смены пароля сотруднику потребуется войти заново.</Alert>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setPasswordDialogOpen(false)} disabled={saving}>Отмена</Button>
          <Button type="submit" variant="contained" disabled={saving}>Изменить пароль</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => !saving && setDeleteDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Удалить сотрудника?</DialogTitle>
        <DialogContent>
          <Typography>
            Аккаунт {selectedStaff?.full_name || selectedStaff?.email}, его активные сессии и назначения будут удалены.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={saving}>Отмена</Button>
          <Button color="error" variant="contained" onClick={deleteSelectedStaff} disabled={saving}>Удалить</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={workspaceDialogOpen}
        onClose={() => !saving && setWorkspaceDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ component: 'form', onSubmit: createWorkspace }}
      >
        <DialogTitle>Новый монтажный объект</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              autoFocus
              fullWidth
              label="Название объекта"
              value={workspaceForm.name}
              onChange={(event) => setWorkspaceForm((form) => ({ ...form, name: event.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setWorkspaceDialogOpen(false)} disabled={saving}>
            Отмена
          </Button>
          <Button type="submit" variant="contained" disabled={saving}>
            Создать
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FieldStaffPage;
